import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { useUser } from "@clerk/clerk-react";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, TextInput, EmptyState, ConfigRequired, SectionTitle, brandName } from "@/lib/ui";
import { CreditCard, IndianRupee } from "lucide-react";

const TONE = { CREATED: "warn", PAID: "ok", FAILED: "err" };

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
    document.body.appendChild(s);
  });
}

export default function Payments() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const { user } = useUser();
  const [form, setForm] = useState({ amount: "", description: "", customer_name: "", customer_email: "", customer_contact: "" });
  const [busy, setBusy] = useState(false);

  const { data: cfg, isLoading: cfgLoading } = useQuery({ queryKey: ["pay-config"], queryFn: () => api.get("/payments/config") });
  const { data: payments = [], isLoading } = useQuery({ queryKey: ["payments", brand], queryFn: () => api.get(`/payments?brand_id=${brand}`) });
  const { data: insights } = useQuery({ queryKey: ["pay-insights", brand], queryFn: () => api.get(`/payments/insights?brand_id=${brand}`) });

  const pay = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt < 1) { toast.error("Enter an amount of at least ₹1"); return; }
    setBusy(true);
    // Pause session recording so analytics never instruments Razorpay's secure payment iframe (breaks OTP/3DS)
    const resumeRec = () => { try { window.posthog?.startSessionRecording?.(); } catch (_) {} };
    try {
      await loadRazorpay();
      const order = await api.post("/payments/order", {
        amount: amt, description: form.description, brand_id: brand,
        customer_name: form.customer_name, customer_email: form.customer_email, customer_contact: form.customer_contact,
      });
      try { window.posthog?.stopSessionRecording?.(); } catch (_) {}
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Gopi Gadhvi Group",
        description: form.description || "Command Centre payment",
        prefill: {
          name: form.customer_name || user?.fullName || "",
          email: form.customer_email || user?.primaryEmailAddress?.emailAddress || "",
          contact: form.customer_contact || "",
        },
        theme: { color: "#d4a63c" },
        handler: async (resp) => {
          resumeRec();
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified & recorded");
            qc.invalidateQueries({ queryKey: ["payments"] });
            qc.invalidateQueries({ queryKey: ["pay-insights"] });
            setForm({ amount: "", description: "", customer_name: "", customer_email: "", customer_contact: "" });
          } catch (e) {
            toast.error(`Verification failed: ${e.message}`);
            qc.invalidateQueries({ queryKey: ["payments"] });
          }
        },
        modal: { ondismiss: () => { resumeRec(); toast.message("Payment cancelled"); } },
      });
      rzp.on("payment.failed", (r) => { resumeRec(); toast.error(`Payment failed: ${r.error?.description || "unknown"}`); });
      rzp.open();
    } catch (e) {
      resumeRec();
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (cfgLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Finance`} title="Payments">
        {cfg?.configured
          ? <StatusBadge tone="ok" dot>Razorpay Live</StatusBadge>
          : <ConfigRequired hint="Add RAZORPAY_KEY_ID / SECRET" />}
      </PageHeader>

      {cfg?.configured && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="payment-insights">
          <Panel className="p-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Collected ({brandName(brand)})</div>
            <div className="font-head text-2xl font-bold mt-2 text-ok" data-testid="insight-total-collected">₹{(insights?.total_collected_inr ?? 0).toLocaleString("en-IN")}</div>
          </Panel>
          <Panel className="p-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Paid</div>
            <div className="font-head text-2xl font-bold mt-2" data-testid="insight-paid">{insights?.count_paid ?? 0}</div>
          </Panel>
          <Panel className="p-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Pending</div>
            <div className="font-head text-2xl font-bold mt-2 text-warn" data-testid="insight-pending">{insights?.count_pending ?? 0}</div>
          </Panel>
          <Panel className="p-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Failed</div>
            <div className="font-head text-2xl font-bold mt-2 text-err" data-testid="insight-failed">{insights?.count_failed ?? 0}</div>
          </Panel>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel className="lg:col-span-4 p-5 h-fit">
          <SectionTitle className="mb-3">Collect a Payment</SectionTitle>
          {!cfg?.configured ? (
            <ConfigRequired hint="Razorpay keys are not set on the server." />
          ) : (
            <div className="space-y-3">
              <TextInput label="Amount (₹)" type="number" min="1" placeholder="500" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
              <TextInput label="Description" placeholder="Consultation fee" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pay-description" />
              <TextInput label="Customer name" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="pay-name" />
              <TextInput label="Customer email" value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="pay-email" />
              <TextInput label="Customer contact" value={form.customer_contact}
                onChange={(e) => setForm({ ...form, customer_contact: e.target.value })} data-testid="pay-contact" />
              <Btn onClick={pay} disabled={busy} className="w-full" data-testid="pay-collect-btn">
                <CreditCard className="h-4 w-4" /> {busy ? "Opening checkout…" : "Collect Payment"}
              </Btn>
              <p className="text-[11px] text-muted-foreground font-mono">Test card: 4111 1111 1111 1111 · any CVV/future date</p>
            </div>
          )}
        </Panel>

        <Panel className="lg:col-span-8 p-0 overflow-hidden">
          <div className="p-4 border-b border-border"><SectionTitle>Payment Log</SectionTitle></div>
          {isLoading ? <PageLoader /> : payments.length === 0 ? (
            <div className="p-4"><EmptyState icon={IndianRupee} title="No payments yet" hint="Collect a payment on the left — verified transactions appear here with their status." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 font-medium">When</th><th className="p-3 font-medium">Amount</th>
                    <th className="p-3 font-medium">Description</th><th className="p-3 font-medium">Customer</th>
                    <th className="p-3 font-medium">Order</th><th className="p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-border/50 hover:bg-accent/30" data-testid={`payment-row-${p.id}`}>
                      <td className="p-3 text-muted-foreground whitespace-nowrap text-xs font-mono">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="p-3 font-mono">₹{p.amount_inr}</td>
                      <td className="p-3">{p.description || "—"}</td>
                      <td className="p-3 text-xs">{p.customer_name || p.customer_email || "—"}</td>
                      <td className="p-3 font-mono text-[11px] text-muted-foreground">{p.order_id}</td>
                      <td className="p-3"><StatusBadge tone={TONE[p.status] || "muted"} dot={p.status === "PAID"}>{p.status}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
